import App from 'app/app.js';
import client from 'braintree-web/client';

class PaymentService {
  constructor(ShoppingCartStore, $http, RouterService, ErrorLogService) {
    this.ShoppingCartStore = ShoppingCartStore;
    this.$http = $http;
    this.paymentInstancePromise = this.createPaymentInstancePromise();
    this.errorLogService = ErrorLogService;
    this.routerService = RouterService;
  }

  getPaymentInstancePromise() {
    if (this.getClientAuthorization()?.clientToken) {
      return this.paymentInstancePromise;
    }

    if (this._paymentInstancePromiseInProgress) {
      return this._paymentInstancePromiseInProgress;
    }

    this._paymentInstancePromiseInProgress = this.createPaymentInstancePromise()
      .then((instance) => {
        this.paymentInstancePromise = Promise.resolve(instance);
        return instance;
      })
      .catch((err) => {
        this.logPaymentRoutingError(err, { flow: 'PaymentService.getPaymentInstancePromise' });
      })
      .finally(() => (this._paymentInstancePromiseInProgress = null));

    return this._paymentInstancePromiseInProgress;
  }

  async createPaymentInstancePromise() {
    const clientAuthorization = await this.getOrCreateClientAuthorization();
    return this.createBraintreeClientWithRetry(clientAuthorization.clientToken);
  }

  createBraintreeClient(authorization) {
    return client.create({ authorization });
  }

  // Braintree's client.create rejects with CLIENT_GATEWAY_NETWORK ("Cannot contact the
  // gateway at this time.") when the browser momentarily cannot reach the Braintree gateway.
  // Retry only that transient error with exponential backoff plus jitter before giving up.
  // Any other error (bad/expired token, etc.) is re-thrown immediately.
  createBraintreeClientWithRetry(authorization, attempt = 1) {
    const MAX_ATTEMPTS = 3;
    const BASE_DELAY_MS = 400;
    const MAX_DELAY_MS = 3000;
    return this.createBraintreeClient(authorization).catch((err) => {
      if (this.isRetryableError(err) && attempt < MAX_ATTEMPTS) {
        const backoff = Math.min(BASE_DELAY_MS * 2 ** (attempt - 1), MAX_DELAY_MS);
        const jitter = Math.random() * 0.3 * backoff;
        return this.delay(backoff + jitter).then(() => this.createBraintreeClientWithRetry(authorization, attempt + 1));
      }
      throw err;
    });
  }

  // Transport-level Braintree failures that are safe to retry. Currently the single
  // transient error this fix targets — the browser momentarily could not reach the
  // Braintree gateway ("Cannot contact the gateway at this time."). Add codes here to
  // widen the retryable set.
  isRetryableError(err) {
    const RETRYABLE_CODES = ['CLIENT_GATEWAY_NETWORK'];
    return !!err && RETRYABLE_CODES.includes(err.code);
  }

  delay(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  getClientAuthorization = function () {
    return this.ShoppingCartStore.getClientAuthorization();
  };

  setClientAuthorization = function (clientAuthorization) {
    this.ShoppingCartStore.setClientAuthorization(clientAuthorization);
  };

  logPaymentRoutingError(error, context = {}) {
    try {
      const history = this.routerService.getHistoryString();
      const contextString = JSON.stringify(context);
      const message = `${error?.message || JSON.stringify(error)} | History: ${history} | Context: ${contextString}`;
      this.errorLogService(new Error(message));
    } catch (logError) {
      const err = `[credit-card.logPopupError] Failed to log error: | logError: ${logError}`;
      this.errorLogService(new Error(err));
    }
  }

  async getOrCreateClientAuthorization() {
    if (this.getClientAuthorization() && this.getClientAuthorization().clientToken) {
      return Promise.resolve(this.getClientAuthorization());
    }

    return this.$http
      .post('checkout/getAuthorization')
      .then((response) => {
        if (response.data.success) {
          this.ShoppingCartStore.setClientAuthorization({
            clientToken: response.data.data.clientToken,
            clientTokenHash: response.data.data.clientTokenHash,
            clientUrl: response.data.data.clientUrl
          });
          this.ShoppingCartStore.setClientTokenHash(response.data.data.clientTokenHash);
        }
        return this.getClientAuthorization();
      })
      .catch((err) => {
        this.logPaymentRoutingError(err, { flow: 'PaymentService.getOrCreateClientAuthorization' });
      });
  }
}

App.service('PaymentService', ['ShoppingCartStore', '$http', 'RouterService', 'ErrorLogService', PaymentService]);
